import React, { useCallback, useEffect, useState } from 'react';

import { twMerge } from 'tailwind-merge';
import { Drawer as DrawerPrimitive } from 'vaul';

import { CrossIcon } from '~shared/assets/icons';

const Drawer: React.FC<React.ComponentProps<typeof DrawerPrimitive.Root>> = ({
  shouldScaleBackground = true,
  noBodyStyles = true,
  ...props
}) => <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} noBodyStyles={noBodyStyles} {...props} />;
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay ref={ref} className={twMerge('fixed inset-0 z-50 bg-black/80', className)} {...props} />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & { hideCloseButton?: boolean; open?: boolean }
>(({ className, children, hideCloseButton = false, open = false, ...props }, ref) => {
  const [style, setStyle] = useState<React.CSSProperties>({});

  const handleKeyboardOpen = useCallback((): void => {
    const windowHeight = window.innerHeight;
    const visualHeight = Telegram.WebApp.viewportStableHeight;

    if (windowHeight - visualHeight > 100) {
      setStyle({
        bottom: `${windowHeight - visualHeight}px`,
        height: `${visualHeight}px`,
        overflow: 'auto',
      });
      setTimeout(() => {
        window.document.body.style.setProperty('top', '1px');
        window.document.body.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }, 200);
    } else {
      setStyle({});
      setTimeout(() => {
        window.document.body.style.setProperty('top', 'unset');
        window.document.body.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }, 200);
    }
  }, []);

  useEffect(() => {
    if (open) {
      window.visualViewport?.addEventListener('resize', handleKeyboardOpen);
    } else {
      setStyle({});
      setTimeout(() => {
        window.document.body.style.setProperty('top', 'unset');
        window.document.body.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }, 200);
    }

    return () => {
      window.visualViewport?.removeEventListener('resize', handleKeyboardOpen);
    };
  }, [handleKeyboardOpen, open]);

  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        ref={ref}
        className={twMerge(
          'fixed inset-x-0 bottom-0 z-50 flex max-h-[80%] flex-col rounded-t-[20px] border-t border-t-blue bg-drawer p-6 pb-12 pt-4 shadow-[0px_0px_90px_0px] shadow-blue outline-none focus:outline-none',
          className
        )}
        style={style}
        {...props}
      >
        <div className="mx-auto mb-4 h-1 w-16 rounded-full bg-white/50" />
        {children}

        {!hideCloseButton && (
          <DrawerClose className="absolute right-6 top-6 text-white/50 transition-all active:opacity-70">
            <CrossIcon className="size-8" />
          </DrawerClose>
        )}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
});
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={twMerge('mb-4 grid gap-2 text-center sm:text-left', className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={twMerge('mt-4 flex flex-col gap-2', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={twMerge('text-center text-2xl font-bold leading-none tracking-tight', className)}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description ref={ref} className={twMerge('text-sm text-muted-foreground', className)} {...props} />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
